import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Select from 'antd/lib/select';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import {
  archiveAnnouncement,
  deleteAnnouncement,
  listAdminAnnouncements,
  publishAnnouncement,
  unpublishAnnouncement,
} from '../api/announcements';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import IconBadge from '../components/helpdesk/IconBadge';
import ManagementPageSkeleton from '../components/management/ManagementPageSkeleton';
import StatsCards from '../components/knowledge-base/StatsCards';
import '../styles/helpdesk.scss';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const priorityOptions = [
  { value: 'all', label: 'All Priority' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

const accessTypeOptions = [
  { value: 'all', label: 'All Access' },
  { value: 'PUBLIC', label: 'Public' },
  { value: 'ROLE_BASED', label: 'Role Based' },
];

function toText(value, fallback = '-') {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value.name) return String(value.name);
    if (value.title) return String(value.title);
    if (value.slug) return String(value.slug);
  }
  return fallback;
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString();
}

function toTimestamp(value) {
  const parsed = new Date(value || '').getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getStatusTone(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'PUBLISHED') return 'success';
  if (normalized === 'ARCHIVED') return 'muted';
  return 'warning';
}

function mapAdminAnnouncementToRow(item) {
  const createdAtRaw = item?.createdAt || item?.publishedAt || item?.updatedAt || '';
  return {
    id: item?.id || item?._id || item?.slug,
    createdAtRaw,
    title: toText(item?.title, 'Announcement'),
    summary: toText(item?.summary || item?.content, ''),
    status: String(item?.status || 'DRAFT').toUpperCase(),
    priority: String(item?.priority || 'MEDIUM').toUpperCase(),
    accessType: String(item?.accessType || 'PUBLIC').toUpperCase(),
    isPinned: Boolean(item?.isPinned),
    createdAt: formatDate(createdAtRaw),
  };
}

function AdminAnnouncementsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [accessTypeFilter, setAccessTypeFilter] = useState('all');

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listAdminAnnouncements({
        page: 1,
        pageSize: 100,
        status: statusFilter,
        priority: priorityFilter,
        accessType: accessTypeFilter,
        q: searchTerm.trim() || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      const sortedByLatestCreated = [...response.items].sort((first, second) => {
        const secondCreatedAt =
          toTimestamp(second?.createdAt) || toTimestamp(second?.publishedAt) || toTimestamp(second?.updatedAt);
        const firstCreatedAt =
          toTimestamp(first?.createdAt) || toTimestamp(first?.publishedAt) || toTimestamp(first?.updatedAt);
        return secondCreatedAt - firstCreatedAt;
      });

      const mapped = sortedByLatestCreated.map(mapAdminAnnouncementToRow);
      setRows(mapped);
      setTotalCount(response.total || mapped.length);
    } catch {
      message.error('Unable to load announcements.');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [accessTypeFilter, priorityFilter, searchTerm, statusFilter]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      loadAnnouncements();
    }, 280);
    return () => clearTimeout(timerId);
  }, [loadAnnouncements]);

  const stats = useMemo(() => {
    const published = rows.filter((item) => item.status === 'PUBLISHED').length;
    const drafts = rows.filter((item) => item.status === 'DRAFT').length;

    return [
      { id: 'total', label: 'Total Announcements', value: totalCount || rows.length, tone: 'default' },
      { id: 'published', label: 'Published', value: published, tone: 'success' },
      { id: 'drafts', label: 'Drafts', value: drafts, tone: 'warning' },
    ];
  }, [rows, totalCount]);

  const handleAction = async (row, action) => {
    if (!row?.id) return;
    setActioningId(row.id);
    try {
      if (action === 'publish') await publishAnnouncement(row.id);
      if (action === 'unpublish') await unpublishAnnouncement(row.id);
      if (action === 'archive') await archiveAnnouncement(row.id);
      if (action === 'delete') await deleteAnnouncement(row.id);
      message.success('Announcement updated.');
      await loadAnnouncements();
    } catch {
      message.error('Unable to update announcement.');
    } finally {
      setActioningId('');
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 470,
      render: (_, row) => (
        <div className="kb-table__title">
          <span>{row.title}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value) => <Tag className={`ann-status-tag ann-status-tag--${getStatusTone(value)}`}>{value}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Access',
      dataIndex: 'accessType',
      key: 'accessType',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Pinned',
      dataIndex: 'isPinned',
      key: 'isPinned',
      width: 90,
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      render: (_, row) => {
        const isBusy = actioningId === row.id;
        const isPublished = row.status === 'PUBLISHED';
        return (
          <div className="kb-table__actions">
            <Button size="small" disabled={isBusy} onClick={() => navigate(`/admin/announcements/edit/${row.id}`)}>
              Edit
            </Button>
            {!isPublished ? (
              <Button size="small" disabled={isBusy} onClick={() => handleAction(row, 'publish')}>
                Publish
              </Button>
            ) : null}
            {isPublished ? (
              <Button size="small" disabled={isBusy} onClick={() => handleAction(row, 'unpublish')}>
                Unpublish
              </Button>
            ) : null}
            <Button size="small" disabled={isBusy} onClick={() => handleAction(row, 'archive')}>
              Archive
            </Button>
            <Popconfirm
              title="Delete announcement"
              description="Are you sure?"
              onConfirm={() => handleAction(row, 'delete')}
              okText="Delete"
              okButtonProps={{ danger: true, loading: isBusy }}
            >
              <Button danger size="small" disabled={isBusy}>
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const getAnnouncementMenuItems = (row) => {
    const items = [{ key: 'edit', label: 'Edit' }];
    if (row.status !== 'PUBLISHED') items.push({ key: 'publish', label: 'Publish' });
    if (row.status === 'PUBLISHED') items.push({ key: 'unpublish', label: 'Unpublish' });
    items.push({ key: 'archive', label: 'Archive' });
    items.push({ key: 'delete', label: 'Delete', danger: true });
    return items;
  };

  const onAnnouncementMenuClick = (row, event) => {
    if (event.key === 'edit') {
      navigate(`/admin/announcements/edit/${row.id}`);
      return;
    }

    if (event.key === 'delete') {
      Modal.confirm({
        title: 'Delete announcement',
        content: 'Are you sure?',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () => handleAction(row, 'delete'),
      });
      return;
    }
    handleAction(row, event.key);
  };

  const showManagementSkeleton = loading && rows.length === 0;

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="kb-page" aria-labelledby="ann-dashboard-title">
            {showManagementSkeleton ? (
              <ManagementPageSkeleton statsCount={3} toolbarCount={4} rowCount={6} />
            ) : (
              <>
                <div className="kb-page__header">
                  <div>
                    <h1 id="ann-dashboard-title">Announcements</h1>
                    <p>Manage and publish all platform announcements</p>
                  </div>
                  <Button
                    type="primary"
                    className="kb-page__action"
                    onClick={() => navigate('/admin/announcements/create')}
                  >
                    Create Announcement
                  </Button>
                </div>

                <section className="kb-page__overview" aria-label="Announcement overview">
                  <div className="kb-page__overview-copy">
                    <h2>Announcement Dashboard</h2>
                    <p>Track announcements, filter by status and priority, and manage publication workflow.</p>
                  </div>
                  <div className="kb-page__overview-meta">
                    <span>{totalCount || rows.length} announcement(s)</span>
                  </div>
                </section>

                <div className="kb-page__workspace">
                  <StatsCards stats={stats} />

                  <section className="kb-articles" aria-label="Announcements table">
                    <div className="kb-articles__header">
                      <h2>Announcement Library</h2>
                      <p>{totalCount || rows.length} total</p>
                    </div>

                    <div className="kb-articles__toolbar kb-articles__toolbar--announcements">
                      <Input.Search
                        placeholder="Search announcements..."
                        enterButton={false}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                      <Select value={statusFilter} options={statusOptions} onChange={setStatusFilter} />
                      <Select
                        value={priorityFilter}
                        options={priorityOptions}
                        onChange={setPriorityFilter}
                      />
                      <Select
                        value={accessTypeFilter}
                        options={accessTypeOptions}
                        onChange={setAccessTypeFilter}
                      />
                    </div>

                    {rows.length || loading ? (
                      <>
                        <Table
                          className="kb-table"
                          rowKey={(record) => record.id}
                          columns={columns}
                          dataSource={rows}
                          scroll={{ x: 1300 }}
                          loading={loading}
                          pagination={{ pageSize: 8 }}
                        />
                        <div className="kb-table-mobile" aria-label="Announcements mobile list">
                          {rows.map((row) => {
                            return (
                              <article key={row.id} className="kb-mobile-card">
                                <div className="kb-mobile-card__head">
                                  <h3 className="kb-mobile-card__title-text">{row.title}</h3>
                                  <div className="kb-mobile-card__head-right">
                                    <Tag className={`ann-status-tag ann-status-tag--${getStatusTone(row.status)}`}>
                                      {row.status}
                                    </Tag>
                                    <Dropdown
                                      menu={{
                                        items: getAnnouncementMenuItems(row),
                                        onClick: (event) => onAnnouncementMenuClick(row, event),
                                      }}
                                      trigger={['click']}
                                      placement="bottomRight"
                                    >
                                      <button
                                        type="button"
                                        className="kb-mobile-card__menu-trigger"
                                        aria-label="Announcement actions"
                                      >
                                        <IconBadge icon="more" />
                                      </button>
                                    </Dropdown>
                                  </div>
                                </div>
                                <p className="kb-mobile-card__text">{row.summary || '-'}</p>
                                <div className="kb-mobile-card__meta">
                                  <span>{row.priority}</span>
                                  <span>{row.accessType}</span>
                                  <span>{row.createdAt}</span>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <Empty description="No announcements found" />
                    )}
                  </section>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default AdminAnnouncementsPage;
